import {
  type FC
} from 'react';
import {
  Box,
  Button,
  Divider,
  Skeleton,
  Stack,
  Typography
} from '@mui/joy';
import {
  alpha
} from '@mui/system';
import {
  BonusTriggerTypeEnum,
  type MakeBonusPlanAwardsData,
  type ChestWithBonuses,
} from '../model';
import {
  RewardInfo
} from './reward-info.component';
import {
  useBonusProgressTracker,
} from '../lib';
import {
  useMakeBonusPlanAwardsMutation
} from '../api';
import {
  smallChest,
  activeCoin,
  inactiveCoin,
  chestBackground,
  woodChestOpened,
  woodChestClosed,
  bronzeChestOpened,
  bronzeChestClosed,
  silverChestClosed,
  silverChestOpened,
} from '../assets';
import {
  BonusChestTimer
} from './bonus-chest-timer.component';

export type ChestImage = {
  opened: string;
  closed: string;
};

const chestsImages: Record<number, ChestImage> = {
  1: {
    opened: woodChestOpened.default,
    closed: woodChestClosed.default,
  },
  2: {
    opened: bronzeChestOpened.default,
    closed: bronzeChestClosed.default,
  },
  3: {
    opened: silverChestOpened.default,
    closed: silverChestClosed.default,
  },
};

export type BonusChestItemProps = {
  partyId: number;
  chestNumber: number;
  chest: ChestWithBonuses;
  isFetching: boolean;
  triggerType: BonusTriggerTypeEnum;
  onBuy?: (purchasesBonusPlanId: number) => void;
  onClaimedFailed?(message?: string): void;
  onClaimedSuccess?(message?: string): void;
};

export const BonusChestItem: FC<BonusChestItemProps> = ({
  chest,
  partyId,
  triggerType,
  chestNumber,
  isFetching,
  onBuy,
  onClaimedFailed,
  onClaimedSuccess
}) => {
  const {
    id,
    title,
    steps,
    endDate,
    closedChestImg,
    openedChestImg,
    claimProgressText,
    redeemableCoinAmount,
    nonRedeemableCoinAmount,
    rewardButtonTextUnclaimed,
  } = chest;
  const [makeBonusPlanAwardsMutation] = useMakeBonusPlanAwardsMutation();
  const { currentProgressStep, isChestOpened, repeat, rewardBonusesCount } = useBonusProgressTracker({ chest });

  const handleClaim = async (): Promise<void> => {
    try {
      const response = await makeBonusPlanAwardsMutation({
        partyId,
        treasureChestId: id,
        triggerType,
      });
      const { data, error: errorData } = response as unknown as { data: MakeBonusPlanAwardsData; error: { error: string; } };
      if (data?.error || errorData?.error || (!data?.awards || !data.awards[0]?.success)) {
        console.error('Error claiming bonus:', data?.error ?? errorData?.error);
        onClaimedFailed?.('Failed to claim bonus');
      } else {
        onClaimedSuccess?.(data?.successMessage || 'Bonus claimed successfully');
      }
    } catch (error) {
      const _error = error instanceof Error ? error : new Error('Something went wrong. Please try again later.');
      console.error('Error claiming bonus:', _error.message);
      onClaimedFailed?.('Failed to claim bonus');
    };
  };

  const handleBonusItemClick = (): void => {
    if (isChestOpened) {
      handleClaim();
    } else {
      onBuy?.(chest.purchasesBonusPlanId);
    }
  };

  return isFetching ? (
    <Skeleton
      variant='rectangular'
      sx={({ breakpoints }) => ({
        width: '100%',
        height: 91,
        borderRadius: 8,
        [breakpoints.down(1024)]: { height: 84 },
        [breakpoints.down(491)]: { height: 8 }
      })} />
  ) : (
    <Stack
      onClick={handleBonusItemClick}
      sx={({ palette, breakpoints }) => ({
        padding: 1.5,
        borderRadius: 8,
        height: '100%',
        cursor: 'pointer',
        overflow: 'hidden',
        position: 'relative',
        boxSizing: 'border-box',
        backgroundColor: palette.common[565],
        '&::before': {
          content: '""',
          top: 0,
          left: 0,
          height: 35,
          width: '100%',
          opacity: 0.45,
          position: 'absolute',
          background: `linear-gradient(180deg, ${alpha(palette.common[315], 0.4)} 0%, ${alpha(palette.common[315], 0)} 100%)`
        },
        [breakpoints.down(491)]: {
          paddingInline: 1,
          paddingBottom: 1
        },
      })}>
      <Stack
        gap={0.2}
        direction='row'
        alignItems='center'
        sx={{
          top: 4,
          right: 4,
          zIndex: 2,
          position: 'absolute',
        }}>
        <Typography
          sx={({ palette }) => ({
            fontSize: 12,
            color: palette.common.white
          })}>
          {rewardBonusesCount}/{repeat + 1}
        </Typography>
        <Box
          width={14}
          height={17}
          marginBottom={0.1}
          component='img'
          src={smallChest.default}
        />
      </Stack>
      <Stack
        gap={1}
        height='100%'
        direction='row'
        alignItems='center'
        justifyContent='space-between'>
        <Stack
          gap={1}
          flexGrow={1}
          height='100%'>
          <Stack
            flexGrow={1}
            direction='row'
            alignItems='center'>
            <Typography
              sx={({ palette, breakpoints }) => ({
                fontWeight: 600,
                textAlign: 'center',
                color: palette.common.white,
                lineHeight: 1,
                [breakpoints.down(1024)]: { fontSize: 12 }
              })}>
              {title}
            </Typography>
            <Divider
              orientation='vertical'
              sx={({ palette, breakpoints }) => ({
                height: 18,
                marginInline: 2,
                marginTop: -0.5,
                backgroundColor: palette.common[600],
                [breakpoints.down(1024)]: { marginInline: 0.5 }
              })}
            />
            <RewardInfo
              redeemableCoinAmount={redeemableCoinAmount}
              nonRedeemableCoinAmount={nonRedeemableCoinAmount}
            />
          </Stack>
          <Stack
            height='100%'
            justifyContent='center'
            sx={({ palette, breakpoints }) => ({
              position: 'relative',
              borderRadius: 8,
              borderWidth: 1,
              paddingInline: 1,
              paddingBlock: 0.5,
              borderStyle: 'solid',
              borderColor: palette.common[600],
              backgroundColor: palette.common[1400],
              boxShadow: `inset 0 4px 4px 0 ${alpha(palette.common[1450], 0.6)}`,
              [breakpoints.down(491)]: {
                paddingInline: 0.5,
              },
              ...(isChestOpened && {
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  borderRadius: 8,
                  top: 0,
                  left: 0,
                  height: '100%',
                  width: '100%',
                  backgroundColor: alpha(palette.common[1400], 0.9),
                },
              })
            })}>
            {isChestOpened && (
              <Button
                size='sm'
                color='success'
                sx={{
                  minWidth: 85,
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }}>
                {rewardButtonTextUnclaimed ?? 'Claim'}
              </Button>
            )}
            <Typography
              sx={({ palette, breakpoints }) => ({
                fontSize: 12,
                fontWeight: 500,
                textAlign: 'center',
                color: palette.common.white,
                [breakpoints.down(1024)]: {
                  fontSize: 10,
                }
              })}>
              {claimProgressText}
            </Typography>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='center'
              gap={steps > 10 ? 0 : 1}>
              {[...Array(steps)].map((_, index) => (
                <Box
                  key={index}
                  component='img'
                  src={(((index + 1 <= currentProgressStep)))
                    ? activeCoin.default
                    : inactiveCoin.default}
                  sx={({ breakpoints }) => ({
                    width: 20,
                    objectFit: 'contain',
                    [breakpoints.down(1024)]: {
                      width: 15,
                    },
                  })}
                />
              ))}
            </Stack>
          </Stack>
        </Stack>
        <Stack
          position='relative'>
          <Box
            component='img'
            src={isChestOpened
              ? (openedChestImg?.url ?? chestsImages[chestNumber]?.opened)
              : (closedChestImg?.url ?? chestsImages[chestNumber]?.closed)}
            sx={({ breakpoints }) => ({
              zIndex: 1,
              width: 65,
              height: 65,
              objectFit: 'cover',
              [breakpoints.down(1024)]: {
                width: 60,
                height: 60,
              },
              [breakpoints.down(491)]: {
                width: 55,
                height: 55,
              },
              ...(!isChestOpened) && {
                animation: 'pulse-chest 3s infinite',
                '@keyframes pulse-chest': {
                  '0%': {
                    transform: 'scale(1)',
                  },
                  '50%': {
                    transform: 'scale(1.1)',
                  },
                  '100%': {
                    transform: 'scale(1)',
                  },
                },
              }
            })}
          />
          <BonusChestTimer endDate={endDate} />
          <Box
            component='img'
            src={chestBackground.default}
            alt='A glowing light effect'
            sx={({ breakpoints }) => ({
              position: 'absolute',
              width: 110,
              height: 110,
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              ...(!isChestOpened) && {
                animation: 'pulse-light 3s infinite',
                '@keyframes pulse-light': {
                  '0%': {
                    opacity: 1,
                  },
                  '50%': {
                    opacity: 0,
                  },
                  '100%': {
                    opacity: 1,
                  },
                },
              },
              [breakpoints.down(1024)]: {
                width: 105,
                height: 105,
              },
              [breakpoints.down(769)]: {
                width: 100,
                height: 100,
              }
            })}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
